import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { QrCode, Monitor, Settings, LogOut, X, BarChart3, Download } from "lucide-react";
import type { Session, DailyMetric, FeedbackItem } from '@/types';
import { useSocket } from '@/hooks/useSocket';
import { WaitList } from '@/components/WaitList';
import { Toaster, toast } from 'sonner';

const API_BASE =
  window.location.hostname === 'localhost'
    ? 'http://localhost:8081/api/v1'
    : '/api/v1';

type Tab = 'waiting' | 'notified' | 'all' | 'analytics';

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('qrhost_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function DashboardPage() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('waiting');
  const [showNewModal, setShowNewModal] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', party_size: 1 });
  const [staff, setStaff] = useState<{ name: string; business_id: string } | null>(null);
  const { connected, lastEvent, joinBusiness } = useSocket();

  // Analytics state
  const [metrics, setMetrics] = useState<DailyMetric[]>([]);
  const [feedbackList, setFeedbackList] = useState<FeedbackItem[]>([]);
  const [feedbackSummary, setFeedbackSummary] = useState({ avg_rating: 0, total: 0 });
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('qrhost_token');
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      const s = JSON.parse(localStorage.getItem('qrhost_staff') || '{}');
      setStaff(s);
    } catch {
      navigate('/login');
    }
  }, [navigate]);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = activeTab === 'all' || activeTab === 'analytics' ? '' : `?status=${activeTab}`;
      const res = await fetch(`${API_BASE}/dashboard/sessions${statusParam}`, {
        headers: { ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error('Error al cargar sesiones');
      const data = (await res.json()) as Session[];
      setSessions(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  const fetchAnalytics = useCallback(async () => {
    if (!staff?.business_id) return;
    setAnalyticsLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const sevenDaysAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];

      const [metricsRes, feedbackRes, summaryRes] = await Promise.all([
        fetch(`${API_BASE}/dashboard/metrics?date_from=${sevenDaysAgo}&date_to=${today}`, {
          headers: getAuthHeaders(),
        }),
        fetch(`${API_BASE}/dashboard/feedback?limit=20`, {
          headers: getAuthHeaders(),
        }),
        fetch(`${API_BASE}/dashboard/feedback/summary`, {
          headers: getAuthHeaders(),
        }),
      ]);

      if (metricsRes.ok) setMetrics((await metricsRes.json()) as DailyMetric[]);
      if (feedbackRes.ok) setFeedbackList((await feedbackRes.json()) as FeedbackItem[]);
      if (summaryRes.ok) setFeedbackSummary(await summaryRes.json());
    } catch {
      // ignore
    } finally {
      setAnalyticsLoading(false);
    }
  }, [staff?.business_id]);

  useEffect(() => {
    if (activeTab === 'analytics') {
      fetchAnalytics();
    } else {
      fetchSessions();
    }
  }, [activeTab, fetchSessions, fetchAnalytics]);

  useEffect(() => {
    if (connected && staff?.business_id) {
      joinBusiness(staff.business_id);
    }
  }, [connected, staff?.business_id, joinBusiness]);

  useEffect(() => {
    if (!lastEvent) return;
    if (lastEvent.event === 'session:updated' || lastEvent.event === 'queue:changed') {
      fetchSessions();
    }
  }, [lastEvent, fetchSessions]);

  const handleNotify = async (session: Session) => {
    try {
      const res = await fetch(`${API_BASE}/sessions/${session.id}/notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({}),
      });
      if (res.ok) fetchSessions();
    } catch {
      // ignore
    }
  };

  const handleSeat = async (session: Session) => {
    try {
      const res = await fetch(`${API_BASE}/sessions/${session.id}/seat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({}),
      });
      if (res.ok) fetchSessions();
    } catch {
      // ignore
    }
  };

  const handleCancel = async (session: Session) => {
    try {
      const res = await fetch(`${API_BASE}/sessions/${session.id}/no-show`, {
        method: 'POST',
        headers: { ...getAuthHeaders() },
      });
      if (res.ok) fetchSessions();
    } catch {
      // ignore
    }
  };

  const handleNewClient = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const body: Record<string, unknown> = {
        client_name: newForm.name,
        party_size: newForm.party_size,
        mode: 'personal',
      };
      if (staff?.business_id) body.business_id = staff.business_id;

      const res = await fetch(`${API_BASE}/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setShowNewModal(false);
        setNewForm({ name: '', party_size: 1 });
        fetchSessions();
        toast.success('Cliente añadido');
      }
    } catch {
      toast.error('Error al añadir cliente');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('qrhost_token');
    localStorage.removeItem('qrhost_refresh_token');
    localStorage.removeItem('qrhost_staff');
    navigate('/login');
  };

  const handleExport = async () => {
    const date = new Date().toISOString().split('T')[0];
    try {
      const res = await fetch(`${API_BASE}/dashboard/export?format=csv&date=${date}`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `qrhost_sessions_${date}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('CSV descargado');
      } else {
        toast.error('Error al exportar');
      }
    } catch {
      toast.error('Error al exportar');
    }
  };

  const filteredSessions =
    activeTab === 'all' || activeTab === 'analytics'
      ? sessions
      : sessions.filter((s) => s.status === activeTab);

  const todayMetric = metrics[metrics.length - 1];
  const totalToday = todayMetric?.total_arrivals || 0;
  const avgWaitToday = todayMetric?.avg_wait_seconds
    ? Math.round(todayMetric.avg_wait_seconds / 60)
    : 0;
  const abandonmentRate = totalToday > 0
    ? Math.round(((todayMetric?.total_cancelled || 0) / totalToday) * 100)
    : 0;

  return (
    <div className="flex flex-col min-h-screen" style={{ background: 'var(--qr-bg)', color: 'var(--qr-text)' }}>
      <Toaster position="top-right" richColors />
      <header
        className="sticky top-0 z-40 flex items-center justify-between px-4 py-3 gap-3"
        style={{ background: 'var(--qr-surface)', borderBottom: '1px solid var(--qr-border)' }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <QrCode size={24} style={{ color: "var(--qr-primary)", flexShrink: 0 }} />
          <span className="font-bold truncate">{staff?.name || 'Dashboard'}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/scan')}
            className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-transform active:scale-[0.96]"
            style={{ background: 'var(--qr-primary)', color: '#fff' }}
          >
            <QrCode size={16} />
            Escanear
          </button>
          <button
            onClick={() => navigate(`/display/${staff?.business_id || ''}`)}
            className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-transform active:scale-[0.96]"
            style={{ background: 'var(--qr-bg)', border: '1px solid var(--qr-border)', color: 'var(--qr-text)' }}
          >
            <Monitor size={16} />
            TV
          </button>
          <button
            onClick={() => navigate('/setup')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-transform active:scale-[0.96]"
            style={{ background: 'var(--qr-bg)', border: '1px solid var(--qr-border)', color: 'var(--qr-text)' }}
          >
            <Settings size={16} />
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-transform active:scale-[0.96]"
            style={{ background: 'var(--qr-bg)', border: '1px solid var(--qr-border)', color: 'var(--qr-danger)' }}
          >
            <LogOut size={16} />
          </button>
        </div>
      </header>

      <div className="flex items-center gap-1 p-2 overflow-x-auto">
        {(['waiting', 'notified', 'all', 'analytics'] as Tab[]).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className="flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap px-3"
            style={{
              background: activeTab === tab ? 'var(--qr-primary)' : 'transparent',
              color: activeTab === tab ? '#fff' : 'var(--qr-text-secondary)',
            }}
          >
            {tab === 'waiting' && 'En espera'}
            {tab === 'notified' && 'Notificados'}
            {tab === 'all' && 'Todos'}
            {tab === 'analytics' && (
              <span className="flex items-center justify-center gap-1.5">
                <BarChart3 size={14} />
                Analíticas
              </span>
            )}
          </button>
        ))}
      </div>

      <main className="flex-1 p-4">
        {activeTab === 'analytics' ? (
          <div className="flex flex-col gap-6 animate-fade-in">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <KpiCard label="Total hoy" value={String(totalToday)} />
              <KpiCard label="Tiempo medio" value={`${avgWaitToday} min`} />
              <KpiCard label="Abandono" value={`${abandonmentRate}%`} />
              <KpiCard
                label="Satisfacción"
                value={`${feedbackSummary.avg_rating.toFixed(1)} ★`}
              />
            </div>

            {/* Chart placeholder */}
            <div
              className="p-4 rounded-2xl"
              style={{ background: 'var(--qr-surface)', border: '1px solid var(--qr-border)' }}
            >
              <h3 className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--qr-text-secondary)' }}>
                Sesiones por hora (últimos 7 días)
              </h3>
              {analyticsLoading ? (
                <div className="h-40 animate-skeleton rounded-xl" style={{ background: 'var(--qr-bg)' }} />
              ) : metrics.length > 0 ? (
                <SimpleBarChart data={metrics} />
              ) : (
                <p className="text-sm" style={{ color: 'var(--qr-text-secondary)' }}>Sin datos aún</p>
              )}
            </div>

            {/* Feedback table */}
            <div
              className="p-4 rounded-2xl"
              style={{ background: 'var(--qr-surface)', border: '1px solid var(--qr-border)' }}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: 'var(--qr-text-secondary)' }}>
                  Feedback reciente
                </h3>
                <button
                  onClick={handleExport}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-transform active:scale-[0.96]"
                  style={{ background: 'var(--qr-bg)', border: '1px solid var(--qr-border)', color: 'var(--qr-text)' }}
                >
                  <Download size={14} />
                  Exportar CSV
                </button>
              </div>
              {analyticsLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-10 animate-skeleton rounded-lg" style={{ background: 'var(--qr-bg)' }} />
                  ))}
                </div>
              ) : feedbackList.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {feedbackList.map((f) => (
                    <div
                      key={f.id}
                      className="flex items-start justify-between p-3 rounded-xl"
                      style={{ background: 'var(--qr-bg)' }}
                    >
                      <div className="flex flex-col gap-0.5">
                        <span className="text-sm font-medium">{f.client_name || 'Cliente'}</span>
                        {f.comment && (
                          <span className="text-xs" style={{ color: 'var(--qr-text-secondary)' }}>
                            {f.comment}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-sm" style={{ color: 'var(--qr-warning)' }}>
                        {'★'.repeat(f.rating)}
                        <span className="text-xs" style={{ color: 'var(--qr-text-secondary)' }}>
                          {new Date(f.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm" style={{ color: 'var(--qr-text-secondary)' }}>Sin feedback aún</p>
              )}
            </div>
          </div>
        ) : (
          <>
            {loading && sessions.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-12 h-12 rounded-xl animate-skeleton" style={{ background: 'var(--qr-surface)' }} />
                  <div className="w-32 h-4 rounded animate-skeleton" style={{ background: 'var(--qr-surface)' }} />
                </div>
              </div>
            ) : (
              <WaitList
                sessions={filteredSessions}
                onNotify={handleNotify}
                onSeat={handleSeat}
                onCancel={handleCancel}
                onNewClient={() => setShowNewModal(true)}
              />
            )}
          </>
        )}
      </main>

      {showNewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div
            className="w-full max-w-sm p-5 rounded-2xl space-y-4 animate-slide-up"
            style={{ background: 'var(--qr-surface)', border: '1px solid var(--qr-border)' }}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">Nuevo cliente manual</h3>
              <button onClick={() => setShowNewModal(false)} className="p-1 rounded-lg" style={{ color: 'var(--qr-text-secondary)' }}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleNewClient} className="flex flex-col gap-3">
              <input
                type="text"
                required
                placeholder="Nombre del cliente"
                value={newForm.name}
                onChange={(e) => setNewForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl outline-none"
                style={{ background: 'var(--qr-bg)', border: '1px solid var(--qr-border)', color: 'var(--qr-text)' }}
              />
              <input
                type="number"
                required
                min={1}
                max={50}
                placeholder="Comensales"
                value={newForm.party_size}
                onChange={(e) => setNewForm((f) => ({ ...f, party_size: Number(e.target.value) }))}
                className="w-full px-4 py-3 rounded-xl outline-none"
                style={{ background: 'var(--qr-bg)', border: '1px solid var(--qr-border)', color: 'var(--qr-text)' }}
              />
              <button
                type="submit"
                className="w-full py-3 rounded-xl font-semibold transition-transform active:scale-[0.98]"
                style={{ background: 'var(--qr-primary)', color: '#fff' }}
              >
                Añadir a la cola
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <div
      className="flex flex-col gap-1 p-4 rounded-2xl"
      style={{ background: 'var(--qr-surface)', border: '1px solid var(--qr-border)' }}
    >
      <span className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--qr-text-secondary)' }}>
        {label}
      </span>
      <span className="text-2xl font-extrabold">{value}</span>
    </div>
  );
}

function SimpleBarChart({ data }: { data: DailyMetric[] }) {
  const max = Math.max(...data.map((d) => d.total_arrivals || 0), 1);
  return (
    <div className="flex items-end gap-2 h-40">
      {data.map((d) => {
        const height = ((d.total_arrivals || 0) / max) * 100;
        const label = new Date(d.date).toLocaleDateString('es-ES', { weekday: 'short' });
        return (
          <div key={d.id} className="flex-1 flex flex-col items-center gap-1">
            <div
              className="w-full rounded-t-lg transition-all duration-500"
              style={{
                height: `${height}%`,
                background: 'var(--qr-primary)',
                minHeight: '4px',
              }}
              title={`${d.total_arrivals} llegadas`}
            />
            <span className="text-[10px] uppercase" style={{ color: 'var(--qr-text-secondary)' }}>
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
}
